import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import siteAnalyticsService, {
  SearchInsightsResponse,
  SearchLogResponse,
  SearchOutcome,
} from '../../services/siteAnalytics.service';
import { useDateRange, RangeSelector, LoadError, fa, faDecimal } from './useDateRange';
import { PALETTE, defaultLineOptions } from './chartConfig';
import './analytics.css';

/**
 * جستجوهای کاربران — تحلیل کامل رفتار جستجو تا خرید:
 * KPI + فانل تبدیل (جستجو → نتیجه → کلیک → سبد → خرید) + روند روزانه +
 * پرتکرارترین‌ها با نرخ تبدیل و درآمد + بی‌نتیجه‌ها (شکاف تقاضا) +
 * محصولات برتر از جستجو + تفکیک منبع/دستگاه + لاگ burst-محور با سرانجام هر جستجو.
 * منبع داده: رویدادهای search_query / search_result_click / add_to_cart / purchase
 * + سفارش‌های پرداخت‌شده (Order/OrderItem) برای اتریبیوشن واقعی خرید.
 */

const LOG_PAGE_SIZE = 30;

const fmtTime = (ts?: string | null) =>
  ts
    ? new Date(ts).toLocaleString('fa-IR', { year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '—';

const toman = (n: number | null | undefined) => (n ? `${fa(n)} تومان` : '—');

const deviceFa = (d?: string | null) => {
  switch (d) {
    case 'mobile': return '📱 موبایل';
    case 'desktop': return '🖥️ دسکتاپ';
    case 'tablet': return 'تبلت';
    case 'bot': return '🤖 بات';
    case 'unknown': return 'نامشخص';
    default: return d || 'نامشخص';
  }
};

const sourceFa = (s?: string | null) => {
  switch (s) {
    case 'navbar': return 'نوار جستجو';
    case 'products_page': return 'صفحهٔ محصولات';
    case 'search_page': return 'صفحهٔ جستجو';
    case 'unknown': return 'نامشخص';
    default: return s || 'نامشخص';
  }
};

const FUNNEL_LABELS: Record<string, string> = {
  search: 'جستجو',
  with_results: 'دارای نتیجه',
  clicked: 'کلیک روی نتیجه',
  carted: 'افزودن به سبد',
  purchased: 'خرید نهایی',
};

const OUTCOME_META: Record<SearchOutcome, { label: string; cls: string }> = {
  purchased: { label: '🛒 خرید شد', cls: 'purchased' },
  carted: { label: 'به سبد رفت', cls: 'carted' },
  clicked: { label: 'کلیک شد', cls: 'clicked' },
  zero: { label: 'بی‌نتیجه', cls: 'zero' },
  none: { label: 'بدون کلیک', cls: 'none' },
};

/** صفحه‌بندی سمت کلاینت برای لیست‌های insights (داده یک‌بار لود شده) */
function usePagedList<T>(items: T[], pageSize = 10) {
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [items]);
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  const p = Math.min(page, pages);
  return {
    rows: items.slice((p - 1) * pageSize, p * pageSize),
    page: p,
    pages,
    setPage,
    total: items.length,
    startIndex: (p - 1) * pageSize,
  };
}

const Pager: React.FC<{ page: number; pages: number; total: number; onPage: (p: number) => void }> = ({
  page,
  pages,
  total,
  onPage,
}) =>
  pages <= 1 ? null : (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
      <button onClick={() => onPage(page - 1)} disabled={page <= 1}>قبلی</button>
      <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
        صفحه {fa(page)} از {fa(pages)} — {fa(total)} مورد
      </span>
      <button onClick={() => onPage(page + 1)} disabled={page >= pages}>بعدی</button>
    </div>
  );

/** جدول تفکیک (منبع/دستگاه) با صفحه‌بندی داخلی */
const BreakdownCard: React.FC<{
  title: string;
  keyHeader: string;
  rows: Array<{ key: string; searches: number; zeroResults: number; clicked: number; purchased: number }>;
  labelOf: (k?: string | null) => string;
}> = ({ title, keyHeader, rows, labelOf }) => {
  const paged = usePagedList(rows, 8);
  return (
    <div className="analytics-section">
      <div className="analytics-section-header">
        <h3>{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="analytics-empty">داده‌ای نیست</div>
      ) : (
        <>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>{keyHeader}</th>
                <th>جستجو</th>
                <th>بی‌نتیجه</th>
                <th>کلیک‌خورده</th>
                <th>به خرید رسیده</th>
              </tr>
            </thead>
            <tbody>
              {paged.rows.map((r) => (
                <tr key={r.key}>
                  <td>{labelOf(r.key)}</td>
                  <td>{fa(r.searches)}</td>
                  <td>
                    {fa(r.zeroResults)}
                    {r.searches > 0 && (
                      <span style={{ color: 'var(--color-text-secondary)', fontSize: 11 }}>
                        {' '}({fa(Math.round((r.zeroResults / r.searches) * 100))}٪)
                      </span>
                    )}
                  </td>
                  <td>{fa(r.clicked)}</td>
                  <td>{r.purchased ? <span className="sa-badge purchased">{fa(r.purchased)}</span> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={paged.page} pages={paged.pages} total={paged.total} onPage={paged.setPage} />
        </>
      )}
    </div>
  );
};

/** خروجی CSV با BOM (اکسل فارسی را درست باز می‌کند) */
const exportCsv = (filename: string, headers: string[], rows: Array<Array<string | number | null | undefined>>) => {
  const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = '﻿' + [headers, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

const SiteAnalyticsSearch: React.FC = () => {
  const dr = useDateRange('30d');
  const [data, setData] = useState<SearchInsightsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reqIdRef = useRef(0);
  const logReqIdRef = useRef(0);

  // ----- لاگ -----
  const [log, setLog] = useState<SearchLogResponse | null>(null);
  const [logLoading, setLogLoading] = useState(false);
  const [logPage, setLogPage] = useState(1);
  const [logQuery, setLogQuery] = useState('');
  const [logQueryInput, setLogQueryInput] = useState('');
  const [logResult, setLogResult] = useState<'all' | 'zero' | 'has'>('all');

  const load = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    try {
      const r = await siteAnalyticsService.getSearchInsights(dr.range);
      if (reqId !== reqIdRef.current) return;
      setData(r);
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت داده');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  const loadLog = useCallback(
    async (page: number, q: string, result: 'all' | 'zero' | 'has') => {
      const reqId = ++logReqIdRef.current;
      setLogLoading(true);
      try {
        const r = await siteAnalyticsService.getSearchLog({
          ...dr.range,
          page,
          limit: LOG_PAGE_SIZE,
          q: q || undefined,
          result: result !== 'all' ? result : undefined,
        });
        if (reqId !== logReqIdRef.current) return;
        setLog(r);
      } catch (e: any) {
        if (reqId !== logReqIdRef.current) return;
        setErr(e?.message || 'خطا در دریافت لاگ جستجو');
      } finally {
        if (reqId === logReqIdRef.current) setLogLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [dr.range.from, dr.range.to]
  );

  useEffect(() => {
    load();
    setLogPage(1);
    loadLog(1, logQuery, logResult);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dr.range.from, dr.range.to, dr.refreshKey]);

  const applyLogFilter = (result?: 'all' | 'zero' | 'has') => {
    const q = logQueryInput.trim();
    const res = result ?? logResult;
    setLogQuery(q);
    if (result) setLogResult(result);
    setLogPage(1);
    loadLog(1, q, res);
  };

  const gotoPage = (p: number) => {
    setLogPage(p);
    loadLog(p, logQuery, logResult);
  };

  const t = data?.totals;
  const funnelBase = data?.funnel?.[0]?.count || 0;

  // لیست‌های صفحه‌بندی‌شده (داده یک‌بار از سرور می‌آید؛ ورق‌زدن آنی است)
  const topQueriesArr = useMemo(() => data?.topQueries || [], [data]);
  const zeroQueriesArr = useMemo(() => data?.zeroResultQueries || [], [data]);
  const topProductsArr = useMemo(() => data?.topProducts || [], [data]);
  const topQPaged = usePagedList(topQueriesArr, 10);
  const zeroPaged = usePagedList(zeroQueriesArr, 10);
  const prodPaged = usePagedList(topProductsArr, 10);

  // برچسب‌های روزانه به جلالی — کلید روز تهران است؛ T12:00:00 مانع جابه‌جایی یک‌روزه می‌شود
  const lineData = data
    ? {
        labels: data.daily.map((d) =>
          new Date(d.date + 'T12:00:00').toLocaleDateString('fa-IR', { month: 'short', day: 'numeric' })
        ),
        datasets: [
          {
            label: 'جستجو',
            data: data.daily.map((d) => d.searches),
            borderColor: PALETTE.primary,
            backgroundColor: PALETTE.primaryLight,
            fill: true,
            tension: 0.3,
          },
          {
            label: 'بی‌نتیجه',
            data: data.daily.map((d) => d.zeroResults),
            borderColor: PALETTE.danger,
            backgroundColor: 'transparent',
            tension: 0.3,
          },
          {
            label: 'کلیک روی نتیجه',
            data: data.daily.map((d) => d.clicks),
            borderColor: PALETTE.purple,
            backgroundColor: 'transparent',
            tension: 0.3,
          },
          {
            label: 'خرید',
            data: data.daily.map((d) => d.purchases),
            borderColor: PALETTE.success,
            backgroundColor: 'transparent',
            tension: 0.3,
          },
        ],
      }
    : null;

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>جستجوهای کاربران</h2>
        <div className="analytics-controls">
          <RangeSelector {...dr} />
          <button onClick={dr.refresh} disabled={loading}>
            {loading ? 'در حال بارگذاری…' : 'بروزرسانی'}
          </button>
        </div>
      </div>

      {err && <LoadError message={err} onRetry={() => { load(); loadLog(logPage, logQuery, logResult); }} />}

      {/* ---------- KPI ---------- */}
      {t && (
        <div className="analytics-kpi-grid">
          <div className="analytics-kpi-card">
            <div className="accent" />
            <div className="label">جستجوهای واقعی</div>
            <div className="value">{fa(t.searches)}</div>
            <span className="change flat">{fa(t.uniqueQueries)} عبارت یکتا · {fa(t.rawEvents || 0)} رویداد خام</span>
          </div>
          <div className="analytics-kpi-card teal">
            <div className="accent" />
            <div className="label">جستجوگر یکتا</div>
            <div className="value">{fa(t.visitors)}</div>
            <span className="change flat">{fa(t.loggedInUsers)} کاربر لاگین‌کرده</span>
          </div>
          <div className="analytics-kpi-card red">
            <div className="accent" />
            <div className="label">جستجوی بی‌نتیجه</div>
            <div className="value">{faDecimal(t.zeroResultPct)}٪</div>
            <span className="change flat">{fa(t.zeroResults)} جستجو بدون نتیجه</span>
          </div>
          <div className="analytics-kpi-card purple">
            <div className="accent" />
            <div className="label">کلیک روی نتیجه (CTR)</div>
            <div className="value">{faDecimal(t.clickThroughPct)}٪</div>
            <span className="change flat">{fa(t.clickedSearches)} جستجو · {fa(t.resultClicks)} کلیک</span>
          </div>
          <div className="analytics-kpi-card green">
            <div className="accent" />
            <div className="label">تبدیل جستجو به خرید</div>
            <div className="value">{faDecimal(t.purchaseConversionPct)}٪</div>
            <span className="change flat">{fa(t.purchasedSearches)} جستجو به خرید رسید</span>
          </div>
          <div className="analytics-kpi-card orange">
            <div className="accent" />
            <div className="label">درآمد از جستجو</div>
            <div className="value">{fa(t.revenue)}</div>
            <span className="change flat">تومان · {fa(t.cartedSearches)} جستجو به سبد رسید</span>
          </div>
        </div>
      )}

      {/* ---------- فانل تبدیل ---------- */}
      {data && funnelBase > 0 && (
        <div className="analytics-section">
          <div className="analytics-section-header">
            <h3>مسیر جستجو تا خرید</h3>
          </div>
          <p className="sa-help">
            هر جستجوی واقعی (یک نوبت تایپ کامل) دنبال می‌شود: نتیجه داشت؟ روی نتیجه کلیک شد؟ به سبد رسید؟ و در نهایت
            خریدِ پرداخت‌شده ثبت شد؟ (اتریبیوشن خرید تا ۷ روز بعد از کلیک، بر اساس سفارش‌های پرداخت‌شدهٔ واقعی)
          </p>
          {data.funnel.map((step, i) => {
            const pctOfFirst = funnelBase ? Math.round((step.count / funnelBase) * 100) : 0;
            const prev = i > 0 ? data.funnel[i - 1].count : step.count;
            const dropPct = prev > 0 ? Math.round(((prev - step.count) / prev) * 100) : 0;
            return (
              <div className="analytics-funnel-step" key={step.step}>
                <div className="name">{FUNNEL_LABELS[step.step] || step.step}</div>
                <div className="bar">
                  <div className="bar-fill" style={{ width: `${Math.max(pctOfFirst, 4)}%` }}>
                    {fa(step.count)} ({fa(pctOfFirst)}٪)
                  </div>
                </div>
                <div className="dropoff">{i > 0 && dropPct > 0 ? `${fa(dropPct)}٪− ریزش` : ''}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---------- روند روزانه ---------- */}
      {lineData && data && data.daily.length > 1 && (
        <div className="analytics-section">
          <div className="analytics-section-header">
            <h3>روند روزانهٔ جستجو</h3>
          </div>
          <div style={{ height: 280 }}>
            <Line data={lineData} options={defaultLineOptions} />
          </div>
        </div>
      )}

      {/* ---------- پرتکرارترین عبارات (با تبدیل) ---------- */}
      {data && (
        <div className="analytics-section">
          <div className="analytics-section-header">
            <h3>پرتکرارترین عبارات جستجو</h3>
            {data.topQueries.length > 0 && (
              <button
                className="sa-export-btn"
                onClick={() =>
                  exportCsv(
                    'search-top-queries.csv',
                    ['عبارت', 'تعداد جستجو', 'کاربر یکتا', 'میانگین نتایج', 'بی‌نتیجه', 'کلیک', 'CTR %', 'سبد', 'خرید', 'درآمد (تومان)', 'آخرین جستجو'],
                    data.topQueries.map((q) => [
                      q.query, q.count, q.visitors, q.avgResults ?? '', q.zeroCount, q.clicks,
                      q.ctrPct, q.carts, q.purchases, q.revenue, new Date(q.lastAt).toISOString(),
                    ])
                  )
                }
              >
                ⬇ خروجی CSV
              </button>
            )}
          </div>
          <p className="sa-help">
            برای هر عبارت: چند بار جستجو شده، به‌طور میانگین چند نتیجه داشته، چند بار روی نتایجش کلیک شده و چند
            جستجو به سبد خرید و خرید نهایی رسیده — دقیق‌ترین منبع برای فهمیدن «مردم دنبال چه هستند و چه می‌خرند».
          </p>
          {data.topQueries.length === 0 ? (
            <div className="analytics-empty">در این بازه جستجویی ثبت نشده</div>
          ) : (
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>عبارت</th>
                    <th>جستجو</th>
                    <th>کاربر</th>
                    <th>میانگین نتایج</th>
                    <th>کلیک (CTR)</th>
                    <th>سبد</th>
                    <th>خرید</th>
                    <th>درآمد</th>
                    <th>آخرین</th>
                  </tr>
                </thead>
                <tbody>
                  {topQPaged.rows.map((q) => (
                    <tr key={q.query}>
                      <td style={{ fontWeight: 600 }}>
                        {q.query}
                        {q.zeroCount > 0 && (
                          <span className="sa-chip" style={{ color: 'var(--color-error-dark, #c62828)' }}>
                            {fa(q.zeroCount)} بی‌نتیجه
                          </span>
                        )}
                      </td>
                      <td>{fa(q.count)}</td>
                      <td>{fa(q.visitors)}</td>
                      <td>{q.avgResults === null ? '—' : fa(q.avgResults)}</td>
                      <td>
                        {fa(q.clicks)}
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: 11 }}> ({faDecimal(q.ctrPct)}٪)</span>
                      </td>
                      <td>{q.carts ? fa(q.carts) : '—'}</td>
                      <td>
                        {q.purchases ? (
                          <span className="sa-badge purchased">{fa(q.purchases)}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{q.revenue ? toman(q.revenue) : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtTime(q.lastAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pager page={topQPaged.page} pages={topQPaged.pages} total={topQPaged.total} onPage={topQPaged.setPage} />
            </div>
          )}
        </div>
      )}

      {data && (
        <div className="analytics-grid-2col">
          {/* ---------- جستجوهای بی‌نتیجه (شکاف تقاضا) ---------- */}
          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>جستجوهای بی‌نتیجه ⚠️ (شکاف تقاضا)</h3>
              {data.zeroResultQueries.length > 0 && (
                <button
                  className="sa-export-btn"
                  onClick={() =>
                    exportCsv(
                      'search-zero-results.csv',
                      ['عبارت', 'تعداد', 'کاربر یکتا', 'الان نتیجه دارد؟', 'آخرین بار'],
                      data.zeroResultQueries.map((q) => [
                        q.query, q.count, q.visitors, q.nowHasResults ? 'بله' : 'خیر',
                        q.lastAt ? new Date(q.lastAt).toISOString() : '',
                      ])
                    )
                  }
                >
                  ⬇ CSV
                </button>
              )}
            </div>
            <p className="sa-help">
              چیزهایی که کاربران می‌خواستند ولی پیدا نکردند — بهترین منبع برای افزودن محصول جدید یا پیشنهاد به
              فروشندگان. اگر «الان نتیجه دارد» سبز باشد یعنی این شکاف بعداً پر شده.
            </p>
            {data.zeroResultQueries.length === 0 ? (
              <div className="analytics-empty">جستجوی بی‌نتیجه‌ای ثبت نشده 🎉</div>
            ) : (
              <div className="analytics-table-wrap">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>عبارت</th>
                      <th>تعداد</th>
                      <th>کاربر</th>
                      <th>وضعیت فعلی</th>
                      <th>آخرین بار</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zeroPaged.rows.map((q) => (
                      <tr key={q.query}>
                        <td style={{ fontWeight: 600 }}>{q.query}</td>
                        <td>{fa(q.count)}</td>
                        <td>{fa(q.visitors)}</td>
                        <td>
                          {q.nowHasResults ? (
                            <span className="sa-badge purchased">الان نتیجه دارد</span>
                          ) : (
                            <span className="sa-badge zero">هنوز بی‌نتیجه</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtTime(q.lastAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pager page={zeroPaged.page} pages={zeroPaged.pages} total={zeroPaged.total} onPage={zeroPaged.setPage} />
              </div>
            )}
          </div>

          {/* ---------- محصولات برتر از جستجو ---------- */}
          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>محصولات برتر از جستجو 🏆</h3>
            </div>
            <p className="sa-help">
              محصولاتی که کاربران از نتایج جستجو روی آن‌ها کلیک کرده‌اند — با تعداد افزودن به سبد، خرید نهایی و
              درآمدِ منتسب به جستجو.
            </p>
            {data.topProducts.length === 0 ? (
              <div className="analytics-empty">در این بازه کلیکی روی نتایج جستجو ثبت نشده</div>
            ) : (
              <div className="analytics-table-wrap">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>محصول</th>
                      <th>کلیک</th>
                      <th>سبد</th>
                      <th>خرید</th>
                      <th>درآمد</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prodPaged.rows.map((p) => (
                      <tr key={p.productId}>
                        <td>
                          <div className="sa-product-cell">
                            {p.image ? <img src={p.image} alt="" loading="lazy" /> : null}
                            <div style={{ minWidth: 0 }}>
                              <div className="t" title={p.name || p.productId}>{p.name || `#${p.productId.slice(-6)}`}</div>
                              {p.topQueries.length > 0 && (
                                <div style={{ fontSize: 10.5, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 210 }}>
                                  {p.topQueries.map((q) => `«${q}»`).join(' ')}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{fa(p.clicks)}</td>
                        <td>{p.carts ? fa(p.carts) : '—'}</td>
                        <td>{p.purchases ? <span className="sa-badge purchased">{fa(p.purchases)}</span> : '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{p.revenue ? toman(p.revenue) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pager page={prodPaged.page} pages={prodPaged.pages} total={prodPaged.total} onPage={prodPaged.setPage} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- تفکیک منبع و دستگاه ---------- */}
      {data && (data.breakdowns.bySource.length > 0 || data.breakdowns.byDevice.length > 0) && (
        <div className="analytics-grid-2col">
          <BreakdownCard
            title="به تفکیک محل جستجو"
            keyHeader="منبع"
            rows={data.breakdowns.bySource}
            labelOf={sourceFa}
          />
          <BreakdownCard
            title="به تفکیک دستگاه"
            keyHeader="دستگاه"
            rows={data.breakdowns.byDevice}
            labelOf={deviceFa}
          />
        </div>
      )}

      {/* ---------- لاگ جستجوها (burst-محور با سرانجام) ---------- */}
      <div className="analytics-section">
        <div className="analytics-section-header">
          <h3>آخرین جستجوها</h3>
        </div>
        <p className="sa-help">
          هر ردیف یک جستجوی واقعی (نوبت کامل تایپ) است — با عبارت نهایی، این‌که نتیجه داشت یا نه، روی کدام محصول
          کلیک شد و سرانجامش چه شد (کلیک / سبد / خریدِ پرداخت‌شده).
        </p>

        <div className="sa-filters">
          <input
            type="text"
            value={logQueryInput}
            onChange={(e) => setLogQueryInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyLogFilter()}
            placeholder="فیلتر بر اساس عبارت جستجو…"
          />
          <select
            value={logResult}
            onChange={(e) => applyLogFilter(e.target.value as 'all' | 'zero' | 'has')}
            disabled={logLoading}
          >
            <option value="all">همهٔ جستجوها</option>
            <option value="zero">فقط بی‌نتیجه‌ها</option>
            <option value="has">فقط دارای نتیجه</option>
          </select>
          <button onClick={() => applyLogFilter()} disabled={logLoading}>اعمال فیلتر</button>
          {(logQuery || logResult !== 'all') && (
            <button
              onClick={() => {
                setLogQueryInput('');
                setLogQuery('');
                setLogResult('all');
                setLogPage(1);
                loadLog(1, '', 'all');
              }}
              disabled={logLoading}
            >
              حذف فیلتر
            </button>
          )}
        </div>

        {logLoading && !log ? (
          <div className="analytics-empty">در حال بارگذاری…</div>
        ) : !log || log.rows.length === 0 ? (
          <div className="analytics-empty">جستجویی مطابق فیلتر پیدا نشد</div>
        ) : (
          <>
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>زمان</th>
                    <th>عبارت</th>
                    <th>نتایج</th>
                    <th>کلیک روی محصول</th>
                    <th>سرانجام</th>
                    <th>کاربر</th>
                    <th>دستگاه · منبع</th>
                  </tr>
                </thead>
                <tbody>
                  {log.rows.map((r) => {
                    const meta = OUTCOME_META[r.outcome] || OUTCOME_META.none;
                    return (
                      <tr key={r.sid}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtTime(r.ts)}</td>
                        <td style={{ fontWeight: 600 }}>
                          {r.query || '—'}
                          {r.events > 1 && (
                            <span className="sa-chip" title="تعداد رویدادهای این نوبت تایپ">{fa(r.events)} رویداد</span>
                          )}
                        </td>
                        <td>
                          {r.resultsCount === null ? (
                            <span title="تعداد کل نتایج ثبت نشده (فقط پیشنهادهای نوار جستجو)">—</span>
                          ) : r.resultsCount === 0 ? (
                            <span className="sa-badge zero">بی‌نتیجه</span>
                          ) : (
                            fa(r.resultsCount)
                          )}
                        </td>
                        <td style={{ maxWidth: 200 }}>
                          {r.click ? (
                            <span
                              style={{ display: 'inline-block', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle', fontSize: 12.5 }}
                              title={r.click.name || r.click.productId}
                            >
                              {r.click.name || `#${r.click.productId.slice(-6)}`}
                              {typeof r.click.rank === 'number' && (
                                <span className="sa-chip">رتبه {fa(r.click.rank)}</span>
                              )}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-text-secondary)' }}>—</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span className={`sa-badge ${meta.cls}`}>{meta.label}</span>
                          {r.outcome === 'purchased' && r.purchaseAmount ? (
                            <div style={{ fontSize: 11, color: 'var(--color-success-dark, #2e7d32)', marginTop: 3 }}>
                              {toman(r.purchaseAmount)}
                              {r.orderRef && <span style={{ color: 'var(--color-text-secondary)' }}> · {r.orderRef}</span>}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {r.user ? (
                            <span>
                              {r.user.name || 'کاربر'}
                              {r.user.phoneNumber && (
                                <span style={{ direction: 'ltr', display: 'inline-block', fontSize: 11, color: 'var(--color-text-secondary)', marginRight: 6 }}>
                                  {r.user.phoneNumber}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-text-secondary)' }} title={`شناسه بازدیدکننده: ${r.visitorId}`}>
                              مهمان
                            </span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                          {deviceFa(r.deviceType)}
                          <span style={{ color: 'var(--color-text-secondary)', fontSize: 11 }}> · {sourceFa(r.source)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
              <button onClick={() => gotoPage(logPage - 1)} disabled={logLoading || logPage <= 1}>قبلی</button>
              <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                صفحه {fa(logPage)} از {fa(log.pages)} — {fa(log.total)} جستجو
              </span>
              <button onClick={() => gotoPage(logPage + 1)} disabled={logLoading || logPage >= log.pages}>بعدی</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SiteAnalyticsSearch;
